// src/pages/Productions/ProductionDetailPage.tsx
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  CalendarDays,
  Clapperboard,
  Clock,
  ExternalLink,
  Handshake,
  MapPin,
  Play,
  User,
} from 'lucide-react';
import StudioShell from '../../components/Studio/StudioShell';
import CastingCallCard from '../../components/Studio/CastingCallCard';
import { StudioErrorState } from '../../components/Studio/StudioStates';
import {
  CONTAINER,
  EYEBROW,
  GHOST_BUTTON,
  PANEL,
  PANEL_HOVER,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
  STAGE_TONE,
} from '../../components/Studio/studioStyles';
import useStudioResource from '../../hooks/useStudioResource';
import useSEO from '../../hooks/useSEO';
import productionService from '../../services/productionService';
import type { Production } from '../../types/studio';

function formatDate(value: string | null): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

function formatDuration(seconds: number | null): string | null {
  if (!seconds || seconds <= 0) return null;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

const ProductionDetailPage = () => {
  const { slug = '' } = useParams<{ slug: string }>();

  const production = useStudioResource<Production>(
    (signal) => productionService.getProduction(slug, signal),
    [slug],
  );

  const data = production.data;

  useSEO({
    title: data ? `${data.title} — StrymHub Studios` : 'Production — StrymHub Studios',
    description:
      data?.logline ||
      data?.synopsis?.slice(0, 160) ||
      'A StrymHub Studios production, with full cast and crew credits.',
    keywords: data
      ? `${data.title}, strymhub studios, ${data.formatLabel}, ${data.genre ?? 'independent film'}, cast and crew`
      : 'strymhub studios production',
    type: 'article',
  });

  if (production.loading) {
    return (
      <StudioShell>
        <div className={`${CONTAINER} py-24`} role="status" aria-label="Loading production">
          <div className="h-4 w-32 animate-pulse rounded bg-white/[0.06]" />
          <div className="mt-6 h-12 w-2/3 animate-pulse rounded bg-white/[0.06]" />
          <div className="mt-4 h-4 w-1/2 animate-pulse rounded bg-white/[0.04]" />
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            <div className="aspect-[2/3] animate-pulse rounded-2xl bg-white/[0.05]" />
            <div className="space-y-4 lg:col-span-2">
              <div className="h-4 animate-pulse rounded bg-white/[0.05]" />
              <div className="h-4 w-5/6 animate-pulse rounded bg-white/[0.05]" />
              <div className="h-4 w-4/6 animate-pulse rounded bg-white/[0.05]" />
            </div>
          </div>
          <span className="sr-only">Loading…</span>
        </div>
      </StudioShell>
    );
  }

  if (production.error || !data) {
    return (
      <StudioShell>
        <div className={`${CONTAINER} py-24`}>
          <StudioErrorState
            message={production.error ?? 'We could not find that production.'}
            onRetry={production.reload}
          />
          <div className="mt-8 flex justify-center">
            <Link to="/productions" className={SECONDARY_BUTTON}>
              Back to all productions
            </Link>
          </div>
        </div>
      </StudioShell>
    );
  }

  const stageTone = STAGE_TONE[data.stage] ?? STAGE_TONE.DEVELOPMENT;
  const shootStart = formatDate(data.shootStartDate);
  const releaseDate = formatDate(data.releaseDate);
  const mainWork = data.works.find((work) => work.role === 'MAIN');
  const extraWorks = data.works.filter((work) => work.role !== 'MAIN');

  return (
    <StudioShell>
      {/* Banner */}
      <div className="relative border-b border-white/10">
        {data.bannerUrl && (
          <>
            <img
              src={data.bannerUrl}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 h-full w-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0D] via-[#0B0B0D]/85 to-[#0B0B0D]/50" />
          </>
        )}

        <div className={`${CONTAINER} relative py-12 sm:py-16`}>
          <Link to="/productions" className={GHOST_BUTTON}>
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            All productions
          </Link>

          <div className="mt-8 grid gap-10 lg:grid-cols-[minmax(0,260px)_1fr]">
            <div className={`${PANEL} overflow-hidden`}>
              <div className="aspect-[2/3] bg-neutral-900">
                {data.posterUrl ? (
                  <img
                    src={data.posterUrl}
                    alt={`Poster for ${data.title}`}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <Clapperboard className="h-12 w-12 text-neutral-700" aria-hidden="true" />
                  </div>
                )}
              </div>
            </div>

            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide ${stageTone}`}
                >
                  {data.stageLabel}
                </span>
                <span className="rounded-full border border-white/10 px-3 py-1 text-xs text-neutral-400">
                  {data.formatLabel}
                </span>
                {data.genre && (
                  <span className="rounded-full border border-white/10 px-3 py-1 text-xs text-neutral-400">
                    {data.genre}
                  </span>
                )}
              </div>

              <h1 className="heading-cinematic mt-5 text-3xl text-white sm:text-4xl lg:text-5xl">
                {data.title}
              </h1>

              {data.logline && (
                <p className="mt-5 max-w-2xl text-lg leading-relaxed text-neutral-300">
                  {data.logline}
                </p>
              )}

              <dl className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-sm text-neutral-400">
                {data.shootLocation && (
                  <div className="inline-flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-neutral-500" aria-hidden="true" />
                    <dt className="sr-only">Shoot location</dt>
                    <dd>{data.shootLocation}</dd>
                  </div>
                )}
                {shootStart && (
                  <div className="inline-flex items-center gap-2">
                    <CalendarDays className="h-4 w-4 text-neutral-500" aria-hidden="true" />
                    <dt className="sr-only">Shoot starts</dt>
                    <dd>Shoots {shootStart}</dd>
                  </div>
                )}
                {releaseDate && (
                  <div className="inline-flex items-center gap-2">
                    <CalendarDays className="h-4 w-4 text-neutral-500" aria-hidden="true" />
                    <dt className="sr-only">Release date</dt>
                    <dd>Released {releaseDate}</dd>
                  </div>
                )}
                {data.runtimeMinutes && (
                  <div className="inline-flex items-center gap-2">
                    <Clock className="h-4 w-4 text-neutral-500" aria-hidden="true" />
                    <dt className="sr-only">Runtime</dt>
                    <dd>{data.runtimeMinutes} min</dd>
                  </div>
                )}
              </dl>

              <div className="mt-9 flex flex-wrap gap-3">
                {mainWork && (
                  <Link to={mainWork.watchPath} className={PRIMARY_BUTTON}>
                    <Play className="h-4 w-4" aria-hidden="true" />
                    Watch {mainWork.title}
                  </Link>
                )}
                {data.trailerUrl && (
                  <a
                    href={data.trailerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={SECONDARY_BUTTON}
                  >
                    Watch the trailer
                    <ExternalLink className="h-4 w-4" aria-hidden="true" />
                  </a>
                )}
                {data.openForCasting && data.openCalls.length > 0 && (
                  <a href="#open-calls" className={SECONDARY_BUTTON}>
                    Open calls ({data.openCalls.length})
                  </a>
                )}
              </div>

              {data.partnerName && (
                <div className={`${PANEL} mt-8 inline-flex items-center gap-3 px-4 py-3`}>
                  <Handshake className="h-4 w-4 text-orange-400" aria-hidden="true" />
                  <p className="text-sm text-neutral-300">
                    Made in partnership with{' '}
                    {data.partnerUrl ? (
                      <a
                        href={data.partnerUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold text-orange-300 underline-offset-4 hover:underline"
                      >
                        {data.partnerName}
                      </a>
                    ) : (
                      <span className="font-semibold text-white">{data.partnerName}</span>
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={`${CONTAINER} py-16`}>
        <div className="grid gap-14 lg:grid-cols-[1fr_minmax(0,340px)]">
          <div className="space-y-14">
            {data.synopsis && (
              <section>
                <h2 className="heading-cinematic text-2xl text-white">The story</h2>
                <p className="mt-5 whitespace-pre-line text-base leading-relaxed text-neutral-300">
                  {data.synopsis}
                </p>
              </section>
            )}

            {extraWorks.length > 0 && (
              <section>
                <h2 className="heading-cinematic text-2xl text-white">From this production</h2>
                <p className="mt-2 text-sm text-neutral-400">
                  Trailers, behind-the-scenes, interviews and clips — everything that came out of
                  the shoot, kept with the production it belongs to.
                </p>

                <ul className="mt-8 grid gap-4 sm:grid-cols-2">
                  {extraWorks.map((work) => (
                    <li key={work.id}>
                      <Link
                        to={work.watchPath}
                        className={`group flex gap-4 overflow-hidden p-3 ${PANEL} ${PANEL_HOVER} focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400`}
                      >
                        <div className="h-20 w-32 shrink-0 overflow-hidden rounded-lg bg-neutral-900">
                          {work.thumbnailUrl ? (
                            <img
                              src={work.thumbnailUrl}
                              alt=""
                              className="h-full w-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center">
                              <Play className="h-5 w-5 text-neutral-700" aria-hidden="true" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-[11px] uppercase tracking-wider text-orange-400">
                            {work.roleLabel}
                          </p>
                          <p className="mt-1 font-display text-sm font-bold text-white line-clamp-2">
                            {work.title}
                          </p>
                          {formatDuration(work.durationSeconds) && (
                            <p className="mt-1 text-xs text-neutral-500">
                              {formatDuration(work.durationSeconds)}
                            </p>
                          )}
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Credits — the whole point of a production page */}
            <section>
              <h2 className="heading-cinematic text-2xl text-white">Cast &amp; crew</h2>
              <p className="mt-2 text-sm text-neutral-400">
                Everyone who made this. Members link through to their own page and their own body
                of work on StrymHub.
              </p>

              {data.creditGroups.length === 0 ? (
                <div className={`${PANEL} mt-8 px-6 py-10 text-center`}>
                  <p className="text-sm text-neutral-400">
                    Credits go up as the production is cast and crewed.
                  </p>
                </div>
              ) : (
                <div className="mt-8 space-y-10">
                  {data.creditGroups.map((group) => (
                    <div key={group.department}>
                      <h3 className={EYEBROW}>{group.departmentLabel}</h3>
                      <ul className="mt-4 divide-y divide-white/[0.07] border-y border-white/[0.07]">
                        {group.credits.map((credit) => {
                          const body = (
                            <>
                              <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/[0.05]">
                                {credit.memberAvatarUrl ? (
                                  <img
                                    src={credit.memberAvatarUrl}
                                    alt=""
                                    className="h-full w-full object-cover"
                                    loading="lazy"
                                  />
                                ) : (
                                  <User className="h-4 w-4 text-neutral-600" aria-hidden="true" />
                                )}
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className="block font-medium text-white">
                                  {credit.creditedName}
                                  {credit.lead && (
                                    <span className="ml-2 rounded bg-orange-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-orange-300">
                                      Lead
                                    </span>
                                  )}
                                </span>
                                <span className="mt-0.5 block text-sm text-neutral-400">
                                  {credit.roleName}
                                  {credit.note ? ` · ${credit.note}` : ''}
                                </span>
                              </span>
                            </>
                          );

                          return (
                            <li key={credit.id}>
                              {credit.memberSlug ? (
                                <Link
                                  to={`/community/troupe/${credit.memberSlug}`}
                                  className="flex items-center gap-4 py-3.5 transition-colors hover:bg-white/[0.03] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400"
                                >
                                  {body}
                                </Link>
                              ) : (
                                <span className="flex items-center gap-4 py-3.5">{body}</span>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>

          {/* Open calls sidebar */}
          <aside id="open-calls" className="space-y-6">
            <h2 className="heading-cinematic text-xl text-white">Open calls</h2>
            {data.openCalls.length === 0 ? (
              <div className={`${PANEL} p-6`}>
                <p className="text-sm leading-relaxed text-neutral-400">
                  Nothing open on this production right now. Join the troupe and you&apos;ll hear
                  about the next one first.
                </p>
                <Link to="/community/join" className={`${SECONDARY_BUTTON} mt-5 w-full`}>
                  Join the troupe
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {data.openCalls.map((call) => (
                  <CastingCallCard key={call.id} call={call} />
                ))}
              </div>
            )}
          </aside>
        </div>
      </div>
    </StudioShell>
  );
};

export default ProductionDetailPage;
